import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, roleHome, startGoogleLogin } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Header";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ email: "", password: "" });
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            const user = await login(form.email, form.password);
            toast.success(`Welcome back, ${user.name}!`);
            navigate(roleHome(user.role));
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="grid min-h-screen place-items-center bg-background px-6 py-12" data-testid="login-page">
            <div className="card-soft w-full max-w-md p-8">
                <Logo />
                <h1 className="mt-8 font-display text-2xl font-bold tracking-tight">Welcome back</h1>
                <p className="mt-1 text-sm text-muted-foreground">Log in to continue your TechBloom journey.</p>
                <form onSubmit={submit} className="mt-8 space-y-5">
                    <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input id="email" data-testid="login-email-input" type="email" required value={form.email}
                            className="rounded-lg focus:ring-2 focus:ring-primary/50"
                            onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="password">Password</Label>
                        <Input id="password" data-testid="login-password-input" type="password" required value={form.password}
                            className="rounded-lg focus:ring-2 focus:ring-primary/50"
                            onChange={(e) => setForm({ ...form, password: e.target.value })} />
                    </div>
                    {error && <p data-testid="login-error" className="rounded-lg bg-accent/10 px-4 py-2.5 text-sm text-accent">{error}</p>}
                    <Button data-testid="login-submit-button" type="submit" disabled={busy} className="w-full rounded-full">
                        {busy ? "Logging in..." : "Log in"}
                    </Button>
                </form>
                <div className="my-6 flex items-center gap-4"><span className="h-px flex-1 bg-border" /><span className="text-xs text-muted-foreground">or</span><span className="h-px flex-1 bg-border" /></div>
                <Button data-testid="login-google-button" variant="outline" className="w-full rounded-full" onClick={startGoogleLogin}>
                    Continue with Google
                </Button>
                <p className="mt-6 text-center text-sm text-muted-foreground">
                    New to TechBloom? <Link to="/register" data-testid="goto-register-link" className="font-semibold text-primary">Create an account</Link>
                </p>
            </div>
        </div>
    );
}
